import { useEffect, useMemo, useState } from "react";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  Chip,
  HeaderBar,
  TableComponent,
  TypoGraph,
} from "components";
import { getPassportReturnNotifications } from "api";
import { IPassportReturnNotification } from "interfaces";
import colors from "theme/colors";
import PassportReturnBlade, { ALL_PAYMENT_TYPES } from "./passportReturnBlade";

const PassportReturnsPage = () => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<IPassportReturnNotification[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [selected, setSelected] = useState<IPassportReturnNotification | null>(
    null
  );

  const load = async () => {
    setLoading(true);
    try {
      const res = await getPassportReturnNotifications();
      setRows(res.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openItem = (row: IPassportReturnNotification) => {
    if (!row.itemId) {
      return;
    }
    window.open(
      `/payments/${row.itemTypeId ?? ALL_PAYMENT_TYPES}/detail/${row.itemId}`,
      "_blank"
    );
  };

  const onUpdated = (updated: IPassportReturnNotification) => {
    setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    setSelected(updated);
  };

  const filteredRows = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    if (!q) {
      return rows;
    }
    return rows.filter((row) =>
      [
        row.passportReturnId,
        row.amount,
        row.method,
        row.returnCode,
        row.returnReason,
        row.itemId,
        row.companyName,
        row.submitterName,
        ...(row.notes || []).map((n) => n.text),
      ]
        .filter((v) => v != null)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [rows, searchValue]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "passportReturnId",
        headerName: "Return ID",
        width: 120,
        hideable: false,
        renderCell: (params: GridRenderCellParams) => (
          <TypoGraph
            variant="body1"
            color={colors.primary}
            link
            onClick={(e) => {
              e.stopPropagation();
              setSelected(params.row as IPassportReturnNotification);
            }}
          >
            {params.value}
          </TypoGraph>
        ),
      },
      { field: "amount", headerName: "Amount", width: 110 },
      { field: "method", headerName: "Method", width: 110 },
      { field: "returnCode", headerName: "Code", width: 90 },
      { field: "returnReason", headerName: "Reason", width: 200 },
      { field: "passportCreatedOn", headerName: "Return Date", width: 170 },
      {
        field: "itemId",
        headerName: "Item",
        width: 110,
        renderCell: (params: GridRenderCellParams) => {
          if (!params.value) {
            return <TypoGraph variant="body1" content="—" />;
          }
          return (
            <TypoGraph
              variant="body1"
              color={colors.primary}
              link
              onClick={(e) => {
                e.stopPropagation();
                openItem(params.row as IPassportReturnNotification);
              }}
            >
              {params.value}
            </TypoGraph>
          );
        },
      },
      {
        field: "companyName",
        headerName: "Company",
        width: 160,
        valueGetter: (params) => params.row.companyName || "—",
      },
      {
        field: "submitterName",
        headerName: "Submitter",
        width: 150,
        valueGetter: (params) => params.row.submitterName || "—",
      },
      {
        field: "sent",
        headerName: "Sent",
        width: 170,
        valueGetter: (params) => params.row.sent || "—",
      },
      {
        field: "completed",
        headerName: "Completed",
        width: 170,
        valueGetter: (params) => params.row.completed || "—",
      },
      {
        field: "fundsCollected",
        headerName: "Funds Status",
        width: 150,
        renderCell: (params: GridRenderCellParams) => (
          <Chip
            label={params.value ? "Collected" : "Outstanding"}
            color={params.value ? "success" : "error"}
            className={params.value ? "successChip" : "errorChip"}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <>
      <HeaderBar title="Passport Returns" />
      <TableComponent
        title="Passport Returns"
        columns={columns}
        data={filteredRows}
        totalCount={filteredRows.length}
        loading={loading}
        searchValue={searchValue}
        isSearchable
        serverPaginationMode={false}
        serverSortingMode={false}
        handleQuickSearch={setSearchValue}
      />
      {selected && (
        <PassportReturnBlade
          key={selected.id}
          row={selected}
          onClose={() => setSelected(null)}
          onUpdated={onUpdated}
          onOpenItem={openItem}
        />
      )}
    </>
  );
};

export default PassportReturnsPage;
